import type { CreateMfaToken } from '@dynamic-labs/sdk-api-core';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';

import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { NoWebAuthNSupportError } from '../../../../errors/NoWebAuthNSupportError';
import { updateAuthFromVerifyResponse } from '../../updateAuthFromVerifyResponse';
import { getPasskeyRegistrationOptions } from '../getPasskeyRegistrationOptions';
import { serverRegisterPasskey } from '../serverRegisterPasskey';

type RegisterPasskeyParams = {
  createMfaToken?: CreateMfaToken;
};

/**
 * Registers a new passkey for the current user.
 *
 * This function creates a new WebAuthn credential that can be used for
 * passwordless authentication. The user will be prompted to create a passkey
 * using their device's biometric authentication or security key.
 *
 * @param [params.createMfaToken] - Optional configuration for MFA token creation.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the registration response.
 * @throws NoWebAuthNSupportError If WebAuthn is not supported by the browser.
 */
export const registerPasskey = async (
  { createMfaToken }: RegisterPasskeyParams = {},
  client = getDefaultClient()
) => {
  const core = getCore(client);

  if (!core.passkey.isSupported()) {
    throw new NoWebAuthNSupportError();
  }

  const options = await getPasskeyRegistrationOptions(client);

  const registration = await core.passkey.register({
    optionsJSON: options as PublicKeyCredentialCreationOptionsJSON,
  });

  const response = await serverRegisterPasskey(
    { createMfaToken, registration },
    client
  );

  updateAuthFromVerifyResponse({ response }, client);

  return response;
};
